import {
  appKey,
  DEFAULT_PRODUCT,
  PLATFORM_NAME,
  productName,
} from '@app/shared/brand'
import { useMutation } from '@tanstack/react-query'
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import {
  AppWindow,
  ArrowLeft,
  Compass,
  Maximize2,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  Users,
  Wrench,
  X,
  Zap,
} from 'lucide-react'
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  type DocGround,
  PageActionGroups,
  usePageAction,
  usePageActionItems,
  usePageBack,
  usePageDoc,
  usePageInfo,
  usePageTitle,
  useSetDocSlot,
  useSetPageEndSlot,
  useSetPagePresenceSlot,
  useSetPageToolSlot,
} from '../../ui'
import { AccountSheet, type AccountTab } from '../../ui/AccountSheet'
import { Assistant } from '../../ui/Assistant'
import { Companion, type CompanionView } from '../../ui/Companion'
import { DocSlotEntry } from '../../ui/DocSlot'
import { ErrorBoundary } from '../../ui/ErrorBoundary'
import { InfoTip } from '../../ui/InfoTip'
import { Logo, type Mark } from '../../ui/Logo'
import { type Surface, UserMenu } from '../../ui/UserMenu'
import { useWatchDocumentExits } from '../../ui/useLeaveDocument'
import { ASSISTANT_VIEW, AssistantProvider, useAssistant } from '../assistant'
import { useAuth } from '../auth'
import { CompanionProvider, useCompanion } from '../companion'
import { DocBarsContext, TogetherContext } from '../doc-bars'
import { docAt, type OpenDoc, openDoc } from '../open-docs'
import { useSpace } from '../org'
import { OrgScopeProvider } from '../org-scope'
import { currentProduct } from '../public-router'
import { useTRPC } from '../trpc'
import { useViewportBand } from '../viewport'
import {
  ActionsSheet,
  DocNavBar,
  DocRail,
  ExitFullScreen,
  FoldTools,
  PhoneBar,
  PhoneSlot,
  RailButton,
  SheetClose,
  TogetherToggle,
  togetherLabel,
  useDocBarState,
  useDocFocus,
  useFullScreen,
} from './DocBars'
import {
  AppSidebar,
  pageTitleFor,
  sectionTabsFor,
  useSidebarCollapse,
} from './nav'

const TOGETHER_KEY = appKey('readTogether')
/**
 * The signed-in app shell: a top bar (brand at the left, user menu at the right,
 * a hairline running the full width beneath) over a left sidebar + content. The
 * path decides the face — platform names itself and carries its own sections;
 * the shul space carries the congregation's.
 */
/**
 * The shell, and the assistant state it lays out around.
 *
 * Split so the provider sits ABOVE the layout that reads it: the shell decides
 * the rail and the width from whether a panel is open, and it cannot read a
 * context it also provides.
 */
export function AppLayout() {
  return (
    <CompanionProvider>
      <AssistantProvider>
        <AppShell />
      </AssistantProvider>
    </CompanionProvider>
  )
}

/** What a document is set on while it loads, until its screen declares it: a book reads on paper, any other lies on the table. */
const GROUND_OF = {
  book: 'paper',
  page: 'table',
  deck: 'table',
  board: 'table',
} as const satisfies Record<OpenDoc['kind'], DocGround>

function AppShell() {
  /* What the shell OFFERS changes at these edges rather than only how it looks,
     which is what this hook is for: below the roomy edge the account is in the
     nav, and below the handheld edge the nav is the drawer. Rendering the copy
     that is not in use leaves controls in the document that nothing opens. */
  const band = useViewportBand()
  const { isPlatformAdmin } = useAuth()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const searchStr = useRouterState({ select: (s) => s.location.searchStr })
  const navigate = useNavigate()
  const onPlatform = pathname.startsWith('/platform') && isPlatformAdmin
  /*
   * Which PRODUCT is being served decides which sections exist, and the host
   * decides the product. An org's work is not a person's, so pointing the shul
   * sections at a personal tenant would resolve nobody and read as an empty
   * shul rather than as a screen that does not belong here.
   *
   * The space follows from the same answer rather than deciding it — see
   * `SpaceProvider`, which resolves the active space against the kind this
   * product works in.
   */
  const surface: Surface = onPlatform
    ? 'platform'
    : currentProduct() === 'studio'
      ? 'studio'
      : 'org'
  const mark: Mark =
    surface === 'platform' ? 'platform' : (currentProduct() ?? DEFAULT_PRODUCT)
  const brandName = mark === 'platform' ? PLATFORM_NAME : productName(mark)
  const { collapsed, toggle } = useSidebarCollapse()
  const pageAction = usePageAction()
  /*
   * How the current collection makes a new record. Declared as a description
   * rather than as buttons (`PageCreate`), so the ranking and the placement are
   * decided here once instead of on every list screen.
   */
  const pageActions = usePageActionItems()
  const setToolSlot = useSetPageToolSlot()
  const setEndSlot = useSetPageEndSlot()
  const setPresenceSlot = useSetPagePresenceSlot()
  const setDocSlot = useSetDocSlot()
  // A screen that names itself (one scheduled message) takes the title slot
  // outright — its own name beats both the route's title and its section's tabs.
  const declaredTitle = usePageTitle()
  const pageInfo = usePageInfo()
  // A record page declares where it goes back to, and takes the leading slot for
  // it — see `PageBack`. The two controls never share the slot.
  const pageBack = usePageBack()
  /*
   * An open document owns the screen. The bar keeps the way back, the
   * document's name, who else is here, its tools and the account; the brand and
   * the nav go, and their width is the document's.
   */
  const declaredDoc = usePageDoc()
  /* The address already says a document is opening, so the shell takes its
     form at once rather than drawing the nav around a loading notice and
     switching once the screen declares itself. What the screen declares
     decides the ground. */
  const routedDoc = docAt(pathname, searchStr)
  const studioDoc =
    declaredDoc ?? (routedDoc ? GROUND_OF[routedDoc.kind] : null)
  useWatchDocumentExits()
  const title = pageTitleFor(pathname, surface)
  // Peer views ride the bar wherever a page has them. The gate was the org
  // surface, which was true only because nothing on the platform had views yet
  // — a surface does not decide whether a subject has more than one of them.
  const { editsDocuments } = useSpace()
  const tabs = declaredTitle
    ? null
    : sectionTabsFor(pathname, { editsDocuments })

  // The band takes its width from somewhere, and a nav nobody is using is the
  // right place: while it is open the rail is forced closed, whatever the
  // person's own preference is, and it returns to that preference the moment
  // they close it.
  const assistant = useAssistant()
  const companion = useCompanion()
  const railClosed = collapsed || companion.active !== null

  /**
   * What is beside the page: the views this page declared, and the assistant
   * once there is a conversation to show. One band draws all of them — a band
   * each would be several competing for the same edge.
   *
   * The assistant comes last because the page's own views are about what is on
   * screen, and it is about what to do with it.
   */
  const bandViews = useMemo<CompanionView[]>(() => {
    const config = assistant.config
    if (!config) return companion.views
    return [
      ...companion.views,
      {
        id: ASSISTANT_VIEW,
        label: config.title,
        icon: <Sparkles size={15} />,
        // Its composer has to stay at the foot of the band while the
        // conversation above it scrolls, so it manages its own inset.
        flush: true,
        render: () => (
          <Assistant key={`${config.skill}:${config.spaceId}`} {...config} />
        ),
      },
    ]
  }, [companion.views, assistant.config])

  /*
   * A Studio document stands between two bars rather than under a top bar: its
   * contents and the open documents on the left, its tools and the band's views
   * on the right.
   */
  const bars = surface === 'studio' && !!studioDoc
  const barState = useDocBarState(band === 'roomy')
  const here = bars ? docAt(pathname, searchStr) : null
  const hereKey = here ? `${here.kind}:${here.ref}:${here.spaceId ?? ''}` : null
  useEffect(() => {
    if (!hereKey) return
    const w = docAt(pathname, searchStr)
    if (w) openDoc(w)
  }, [hereKey, pathname, searchStr])
  const navViews = useMemo(
    () => (bars ? bandViews.filter((v) => v.place === 'nav') : []),
    [bars, bandViews],
  )
  const toolViews = useMemo(
    () => (bars ? bandViews.filter((v) => v.place !== 'nav') : bandViews),
    [bars, bandViews],
  )
  const { setMode, setNavOpen: setDocNavOpen } = barState
  const fullScreen = useFullScreen()
  const focusTo = useDocFocus()
  /* Full screen comes and goes by its controls and by Escape alike, so focus
     follows the state rather than any one of them: into the way out, and back
     to the way in. */
  const fullWas = useRef(fullScreen.full)
  useEffect(() => {
    if (fullWas.current === fullScreen.full) return
    fullWas.current = fullScreen.full
    if (fullScreen.full) focusTo('exit-full')
    else focusTo('enter-full', 'phone-tools')
  }, [fullScreen.full, focusTo])
  /*
   * A page asking for its contents is asking for the left bar: it opens there
   * at the contents, and the band stays as it was.
   */
  useEffect(() => {
    if (!bars) return
    const asked = companion.active
    if (asked && navViews.some((v) => v.id === asked)) {
      setMode('contents')
      setDocNavOpen(true)
      companion.close()
    }
  }, [bars, companion, navViews, setMode, setDocNavOpen])
  /* The band is out while one of its views is open, or while the right bar
     was opened for its head alone — a document with no views to open it at. */
  const [toolsBare, setToolsBare] = useState(false)
  const toolShowing =
    bars && (toolsBare || toolViews.some((v) => v.id === companion.active))
  /* Folding the band hands focus to the rail icon that reopens what it showed. */
  const closeTools = () => {
    focusTo(`tools-${companion.active}`, 'phone-tools', 'enter-full')
    setToolsBare(false)
    companion.close()
  }
  const openTools = () => {
    const first = toolViews[0]
    if (first) companion.open(first.id)
    else setToolsBare(true)
    focusTo('tools-fold')
  }
  /* In a hand the two rails are one, across the top. */
  const handheld = band === 'handheld'
  /* Reading together is the person's to switch on, and stays as they left it.
     A document with nobody to read with reports null, and offers no switch. */
  const [togetherOn, setTogetherOnRaw] = useState(() => {
    try {
      return localStorage.getItem(TOGETHER_KEY) === '1'
    } catch {
      return false
    }
  })
  const setTogetherOn = useCallback((on: boolean) => {
    setTogetherOnRaw(on)
    try {
      localStorage.setItem(TOGETHER_KEY, on ? '1' : '0')
    } catch {
      /* Held for this visit only. */
    }
  }, [])
  const [others, setOthers] = useState<number | null>(null)
  const together = useMemo(
    () => ({ on: togetherOn, setOn: setTogetherOn, report: setOthers }),
    [togetherOn, setTogetherOn],
  )
  const togetherToggle = (place: 'rail' | 'head') =>
    others === null ? null : (
      <TogetherToggle
        on={togetherOn}
        others={others}
        onToggle={() => setTogetherOn(!togetherOn)}
        place={place}
      />
    )
  /* Over the paper a bar is for the moment: going somewhere folds it. */
  const roomy = band === 'roomy'
  useEffect(() => {
    if (!roomy && pathname) setDocNavOpen(false)
  }, [roomy, pathname, setDocNavOpen])
  const openNav = (m: 'contents' | 'open') => {
    setMode(m)
    setDocNavOpen(true)
    focusTo('nav-fold')
  }
  /* Folding the bar hands focus to the rail icon that reopens what it showed. */
  const foldNav = () => {
    setDocNavOpen(false)
    focusTo(`nav-${barState.mode}`, 'nav-open', 'phone-nav')
  }
  /* In a hand the Actions are a sheet of their own, and reading together is
     one of them: switched there, its bar at the foot carries its close. */
  const [actionsOpen, setActionsOpen] = useState(false)
  useEffect(() => {
    if (pathname) setActionsOpen(false)
  }, [pathname])
  const closeActions = useCallback(() => {
    setActionsOpen(false)
    focusTo('phone-actions')
  }, [focusTo])
  const phoneActions = useMemo(
    () =>
      others === null
        ? pageActions
        : [
            ...pageActions,
            {
              id: 'together',
              type: 'view' as const,
              label: togetherLabel(togetherOn),
              icon: Users,
              onSelect: () => setTogetherOn(!togetherOn),
            },
          ],
    [pageActions, others, togetherOn, setTogetherOn],
  )
  /* One sheet at a time: raising one puts the other two away. */
  const raisePhoneSheet = (sheet: 'nav' | 'tools' | 'actions') => {
    if (sheet !== 'nav') setDocNavOpen(false)
    if (sheet !== 'tools' && toolShowing) closeTools()
    if (sheet !== 'actions') setActionsOpen(false)
  }

  /*
   * The drawer, for the handheld band. Deliberately not conditioned on the band
   * in JS: CSS decides whether the brand column shows a hamburger or a collapse
   * toggle, and whether the nav is a drawer or a column. This state only says
   * whether the person has opened it.
   */
  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => {
    if (!navOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  /*
   * One account control, rendered in two places and shown in one.
   *
   * The top bar carries it above the handheld edge and the drawer carries it
   * below, and CSS picks — the same arrangement as the nav itself. Declared once
   * here rather than built twice, so the surface switch and the sign-out cannot
   * drift between them.
   */
  /*
   * The account is the person's, not the app's, so the shell holds it open and
   * every surface reaches the same one.
   */
  const [accountOpen, setAccountOpen] = useState(false)
  /* The area the account opens on. Every notice's mail links here with
     `?account=notifications`, since the account is drawn by the shell and has
     no address of its own; the parameter opens it once and is then dropped,
     so a reload does not open it again. */
  const [accountTab, setAccountTab] = useState<AccountTab>('signin')
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get('account') !== 'notifications') return
    setAccountTab('notifications')
    setAccountOpen(true)
    url.searchParams.delete('account')
    window.history.replaceState(window.history.state, '', url)
  }, [])
  /*
   * Which product's ground the page is drawn on, set on the ROOT: a Sheet and a
   * Menu portal out of the shell, and a ground that stopped at `.app` would
   * leave them on another product's colors.
   */
  useEffect(() => {
    const root = document.documentElement
    if (surface !== 'studio') return
    root.setAttribute('data-product', 'studio')
    return () => root.removeAttribute('data-product')
  }, [surface])
  const trpc = useTRPC()
  const handoff = useMutation(trpc.auth.handoff.mutationOptions())
  const accountMenu = (
    <UserMenu
      onAccount={() => {
        setAccountTab('signin')
        setAccountOpen(true)
      }}
      /*
       * Each app is a site of its own, so this leaves rather than routes — and
       * it takes the session with it: a cookie cannot span our domains, so the
       * API mints a one-minute token for that origin and the address we land on
       * redeems it there. Somebody switching products never meets a sign-in
       * screen.
       *
       * It lands on that site's root and lets the site decide where that is;
       * naming a path here would be this app holding an opinion about another
       * one's landing, which is the thing separate origins settle.
       *
       * A refused handoff still goes: the person arrives signed out and signs
       * in there, which is the behavior without this and not a dead end.
       */
      onSwitch={(origin) => {
        handoff.mutate(
          { to: origin },
          {
            onSuccess: (r) => window.location.assign(r.url),
            onError: () => window.location.assign(origin),
          },
        )
      }}
    />
  )

  return (
    /* Which orgs a view shows is a question only an org's surface asks. */
    <OrgScopeProvider active={surface === 'org'}>
      <DocBarsContext.Provider value={bars}>
        <TogetherContext.Provider value={bars ? together : null}>
          <div
            data-chrome={bars ? 'bars' : undefined}
            className={`app${railClosed ? ' app--sidebar-collapsed' : ''}${
              assistant.config ? ' app--assistant-open' : ''
            }${navOpen ? ' app--nav-open' : ''}`}
            data-surface={surface}
            data-form={studioDoc ? 'doc' : undefined}
            data-ground={studioDoc ?? undefined}
          >
            {bars ? null : (
              <header className="topbar">
                {studioDoc ? null : (
                  <div className="topbar__brand">
                    {/* The chrome names the app you are IN, which is the product this
              host serves — or the platform, on its own. A wordmark that named
              one product over another's screens is the app telling you where
              you are and getting it wrong. */}
                    <span className="topbar__mark">
                      <Logo product={mark} />
                    </span>
                    <span className="topbar__wordmark">{brandName}</span>
                    {/* One slot, two jobs, decided by width in CSS: above the handheld
              edge the brand column collapses the rail; below it, where there is
              no room for a rail beside the content, it opens the drawer. */}
                    <button
                      type="button"
                      className="topbar__collapse"
                      onClick={(e) => {
                        toggle()
                        /*
                         * A pointer leaves focus behind on the button it pressed, and in
                         * the collapsed rail focus is what holds this control in front of
                         * the logo — so a click would keep it there until you clicked
                         * something else. `detail > 0` is the pointer; a keyboard press
                         * reports 0 and keeps its focus, which is the whole point of it.
                         */
                        if (e.detail > 0) e.currentTarget.blur()
                      }}
                      aria-label={
                        collapsed ? 'Expand sidebar' : 'Collapse sidebar'
                      }
                    >
                      {collapsed ? (
                        <PanelLeftOpen size={16} aria-hidden />
                      ) : (
                        <PanelLeftClose size={16} aria-hidden />
                      )}
                    </button>
                    {/*
            One leading control, never two.

            A page about one record is somewhere you arrived from a list, so the
            move that matters is back to that list — and the global nav is one tap
            further through it. Giving the slot to a single control also makes it
            twice the target, which is the whole argument on a phone.

            The back control itself is drawn beside the TITLE, in the content
            column, because it is page context rather than brand chrome — and
            because this column is hidden above the handheld edge, which left a
            record page with no way back at all on a desktop.
          */}
                    {pageBack ? null : (
                      <button
                        type="button"
                        className="topbar__nav-toggle"
                        onClick={() => setNavOpen((o) => !o)}
                        aria-label={
                          navOpen ? 'Close navigation' : 'Open navigation'
                        }
                        aria-expanded={navOpen}
                        /* Names what it opens. A disclosure that states only that it is
                 expanded leaves a reader to guess what expanded, and leaves
                 nothing in the document connecting this control to the sections
                 it reveals. */
                        aria-controls="app-sidebar"
                      >
                        {navOpen ? (
                          <X size={18} aria-hidden />
                        ) : (
                          <Menu size={18} aria-hidden />
                        )}
                      </button>
                    )}
                  </div>
                )}
                <div className="topbar__main">
                  {/* The way out of one record, leading its title — the same slot on every
              detail screen, at every width. */}
                  {pageBack ? (
                    <button
                      type="button"
                      className="topbar__back"
                      onClick={pageBack}
                      aria-label="Back"
                    >
                      <ArrowLeft size={20} aria-hidden />
                    </button>
                  ) : null}
                  {tabs ? (
                    <nav className="topbar__tabs" aria-label="Section views">
                      {tabs.map((t) => (
                        <Link
                          key={t.to}
                          to={t.to}
                          className="topbar__tab"
                          /* Exact by default, or every tab whose path is a prefix of
                     the current one lights up too. A `nested` tab opts back in,
                     so a detail page beneath it keeps its own tab marked.

                     `includeSearch: false` because a tab is a PLACE and the
                     query is what you are doing there — matching the search
                     too, typing in the box unmarked the tab you were on. */
                          activeOptions={{
                            exact: !t.nested,
                            includeSearch: false,
                          }}
                          activeProps={{ 'aria-current': 'page' }}
                          /* Another view of the section you are in is not a place to
                     go back to, so it REPLACES: Back returns to whatever you
                     were doing before you arrived, rather than walking the
                     strip tab by tab. */
                          replace
                        >
                          {t.label}
                        </Link>
                      ))}
                    </nav>
                  ) : (
                    <h1 className="topbar__title">{declaredTitle ?? title}</h1>
                  )}
                  {/* Beside whichever of the two occupies the title slot — a page with
              tabs has a blurb as often as one without. */}
                  {pageInfo ? (
                    <InfoTip size="inline" label="About this page">
                      {pageInfo}
                    </InfoTip>
                  ) : null}
                  {/*
            The bar's budget is fixed at every width: the nav control, the page's
            identity, ONE action entry point, and the account. Whatever a screen
            declares — one action or five — is one measured group, so adding an
            action cannot change the layout.

            Actions are chrome, so a short bar collapses the whole group rather
            than wrapping or shrinking it, and the fit is measured: what has to
            fit is the buttons a screen declared, and no width in a stylesheet
            knows how wide those are.
          */}
                  {/* Always rendered, even with nothing in it: `:empty` is what hands the
              trailing edge to the account, and a slot that is absent rather than
              empty matches nothing. The group goes inside it and only when there
              is something to hold, so an empty slot stays genuinely empty. */}
                  {/* Who else is on this page, ahead of the page's own controls: the
              people first, then what there is to do, then who I am. Empty on
              every screen with nobody to show. */}
                  <div className="topbar__presence" ref={setPresenceSlot} />
                  {/* No collapsing group: a screen's actions arrive as one named menu,
              and a group whose whole job is turning several controls into one
              has nothing to do around a single one. */}
                  <div className="topbar__actions">
                    {pageAction}
                    <PageActionGroups items={pageActions} />
                  </div>
                  {/* The account leaves for the left nav below the roomy edge, where it
              sits above the org chooser — the two answer the same question. It
              LEAVES rather than hides: one account in the document, wherever it
              is, so neither copy is a control standing somewhere unreachable. */}
                  {/* An open document has no nav to carry the account, so the
              band keeps it at every width. */}
                  {band === 'roomy' || studioDoc ? (
                    <div className="topbar__user">{accountMenu}</div>
                  ) : null}
                </div>
              </header>
            )}
            {/*
        The handheld toolbar.

        The header above it carries the menu button, the way back and the page's
        name, and nothing else — three things, one of which is a label. Anything
        a screen wants to offer comes here instead, on a band of its own directly
        beneath, where it has the width to be a real target instead of competing
        with the title for a corner.

        Rendered by the shell rather than by each screen, so it is the same band
        on every page and a screen declares its actions exactly once. It is one
        row tall whatever it holds, because each cell is one control that
        collapses rather than wrapping — which is what makes `--topbar-h` safe
        to grow by a fixed amount for it.
      */}
            {bars ? null : (
              <div className="app-toolbar">
                {/*
          The list's own tools, portalled in by its TableHeader. They lead,
          because the search is the one control you reach for without looking —
          and because a strip of its own beneath this row would be the second
          story of chrome this row exists to prevent.
        */}
                <div className="app-toolbar__tools" ref={setToolSlot} />
                {pageAction}
                {/*
          The page's actions, as a group that MEASURES. Two of them fit beside a
          search on a tablet and not on a 375px phone, and a threshold cannot tell
          those apart — so the group collapses to one entry point exactly when its
          contents stop fitting, whatever a screen happens to declare.
        */}
                {/*
          While one action is holding something open, the way out TAKES the
          cell rather than hiding inside it.

          A screen's actions are ONE control here, as they are in the bar: the
          menu that holds them, or the single Close while something is open. A
          collapsing group around it would open a panel whose only contents is a
          trigger opening a second panel.
        */}
                {pageActions.length > 0 ? (
                  <PageActionGroups items={pageActions} />
                ) : null}
                {/*
          The trailing end of the row, for what opens at the page's trailing
          edge — a companion band, which is reached from the same side shut as
          open. A slot of its own rather than the tools slot reordered, because
          the hairline between cells follows DOM order.
        */}
                <div className="app-toolbar__end" ref={setEndSlot} />
              </div>
            )}
            <div className="shell">
              {bars && !fullScreen.full ? (
                barState.navOpen ? (
                  <DocNavBar
                    /* A document names itself; the section's title would misname
                       it while it loads. */
                    title={declaredTitle}
                    onBack={pageBack ?? (() => navigate({ to: '/documents' }))}
                    views={navViews}
                    mode={barState.mode}
                    onMode={setMode}
                    current={here}
                    onClose={foldNav}
                    phone={handheld}
                    foot={handheld ? accountMenu : undefined}
                  />
                ) : handheld ? null : (
                  <DocRail side="nav" label="Document">
                    <RailButton
                      label="Back"
                      onClick={
                        pageBack ?? (() => navigate({ to: '/documents' }))
                      }
                    >
                      <ArrowLeft size={16} aria-hidden />
                    </RailButton>
                    {navViews[0] ? (
                      <RailButton
                        label={navViews[0].label}
                        focusKey="nav-contents"
                        onClick={() => openNav('contents')}
                      >
                        {navViews[0].icon}
                      </RailButton>
                    ) : null}
                    <RailButton
                      label="Open documents"
                      focusKey="nav-open"
                      onClick={() => openNav('open')}
                    >
                      <AppWindow size={16} aria-hidden />
                    </RailButton>
                  </DocRail>
                )
              ) : null}
              {/* One nav in the document at a time. Below the handheld edge the
            column has no place in the flow and the drawer is the whole of the
            nav, so rendering the column anyway leaves a hidden copy of every
            destination that nothing opens — dead weight to the reader, and to
            anything reading the page it is a set of links out of reach. */}
              {band === 'handheld' || studioDoc ? null : (
                <AppSidebar
                  surface={surface}
                  collapsed={railClosed}
                  account={accountMenu}
                />
              )}
              {/* The drawer: the same nav, over the content, for the handheld band.
            A second element rather than the first one repositioned, because the
            column has to stay in the flow for the layout above the edge and the
            drawer has to leave it. CSS shows exactly one of them.

            It scrims, and that is not a contradiction of the companion's rule:
            a drawer interrupts — you are picking where to go and the page you
            were on is not the context for that choice. */}
              {navOpen && !studioDoc ? (
                <>
                  <button
                    type="button"
                    className="app-nav-scrim"
                    aria-label="Close navigation"
                    onClick={() => setNavOpen(false)}
                  />
                  <AppSidebar
                    surface={surface}
                    collapsed={false}
                    drawer
                    onPick={() => setNavOpen(false)}
                    account={accountMenu}
                  />
                </>
              ) : null}
              <main className="shell__main">
                {/* What is said about the open document, above it: who is here
                  while reading together is on, then whatever the document
                  declares — see `DocSlot`. */}
                {bars && !fullScreen.full ? (
                  <div className="docslot" ref={setDocSlot}>
                    {together.on && others !== null ? (
                      <DocSlotEntry
                        kind="together"
                        ref={setPresenceSlot}
                        onClose={
                          handheld ? () => setTogetherOn(false) : undefined
                        }
                        closeLabel="Stop reading together"
                      />
                    ) : null}
                  </div>
                ) : null}
                {/* One screen failing is not the app failing: the boundary sits
              inside the shell so the nav, the tabs and the account survive a
              crash in the content, and moving to another page clears it. */}
                <ErrorBoundary resetKey={pathname}>
                  <Suspense
                    fallback={
                      <div className="route-fallback" aria-busy="true" />
                    }
                  >
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </main>
              {/* A sibling of the content, not a layer over it — which is what makes
            the rows beside it readable while a change is being discussed. */}
              {bars ? (
                fullScreen.full ? (
                  <ExitFullScreen onExit={fullScreen.exit} />
                ) : toolShowing ? (
                  <Companion
                    views={toolViews}
                    active={companion.active ?? ''}
                    onActiveChange={companion.open}
                    onClose={closeTools}
                    lead={
                      handheld ? (
                        /* Risen over the document in a hand the sheet is the
                           tools alone; what else the bar's head holds has a
                           slot of its own. */
                        <div className="comp__lead-head">
                          <span className="comp__lead-gap" />
                          <SheetClose
                            label="Close"
                            focusKey="tools-fold"
                            onClose={closeTools}
                          />
                        </div>
                      ) : (
                        <div className="comp__lead-head">
                          {/* The fold sits on the bar's inner edge, beside the
                            paper, as the left bar's does. */}
                          <FoldTools onClose={closeTools} />
                          <span className="comp__lead-gap" />
                          {togetherToggle('head')}
                          {pageAction}
                          <PageActionGroups items={pageActions} compact />
                          {accountMenu}
                        </div>
                      )
                    }
                  />
                ) : handheld ? null : (
                  <DocRail side="tools" label="Tools">
                    <div className="docrail__cell">{accountMenu}</div>
                    {togetherToggle('rail')}
                    {pageAction ? (
                      <div className="docrail__cell">{pageAction}</div>
                    ) : null}
                    {pageActions.length ? (
                      <div className="docrail__cell">
                        <PageActionGroups items={pageActions} compact />
                      </div>
                    ) : null}
                    {toolViews.map((v) => (
                      <RailButton
                        key={v.id}
                        label={
                          v.count
                            ? `${v.label} — ${v.countLabel ?? v.count}`
                            : v.label
                        }
                        focusKey={`tools-${v.id}`}
                        mark={v.alert}
                        onClick={() => {
                          companion.open(v.id)
                          focusTo('tools-fold')
                        }}
                      >
                        {v.icon}
                      </RailButton>
                    ))}
                    <RailButton
                      label="Full screen"
                      focusKey="enter-full"
                      onClick={fullScreen.enter}
                    >
                      <Maximize2 size={16} aria-hidden />
                    </RailButton>
                  </DocRail>
                )
              ) : null}
              {/* In a hand the document has one bar across the top, and each
                slot opens a sheet rising from the foot. One sheet at a time. */}
              {bars && handheld && !fullScreen.full ? (
                <PhoneBar>
                  <PhoneSlot
                    label="Back"
                    icon={<ArrowLeft size={16} aria-hidden />}
                    onClick={pageBack ?? (() => navigate({ to: '/documents' }))}
                  />
                  <PhoneSlot
                    label="Navigate"
                    icon={<Compass size={16} aria-hidden />}
                    focusKey="phone-nav"
                    open={barState.navOpen}
                    onClick={() => {
                      if (barState.navOpen) return foldNav()
                      raisePhoneSheet('nav')
                      setDocNavOpen(true)
                      focusTo('nav-fold')
                    }}
                  />
                  {toolViews.length ? (
                    <PhoneSlot
                      label="Tools"
                      icon={<Wrench size={16} aria-hidden />}
                      focusKey="phone-tools"
                      open={toolShowing}
                      mark={
                        toolViews.some((v) => v.alert) ? 'warning' : undefined
                      }
                      onClick={() => {
                        if (toolShowing) return closeTools()
                        raisePhoneSheet('tools')
                        openTools()
                      }}
                    />
                  ) : null}
                  {phoneActions.length ? (
                    <PhoneSlot
                      label="Actions"
                      icon={<Zap size={16} aria-hidden />}
                      focusKey="phone-actions"
                      open={actionsOpen}
                      mark={
                        pageActions.some((a) => (a.waiting ?? 0) > 0) ||
                        (!togetherOn && (others ?? 0) > 0)
                          ? 'ink'
                          : undefined
                      }
                      onClick={() => {
                        if (actionsOpen) return closeActions()
                        raisePhoneSheet('actions')
                        setActionsOpen(true)
                        focusTo('actions-close')
                      }}
                    />
                  ) : null}
                </PhoneBar>
              ) : null}
              {bars &&
              handheld &&
              !fullScreen.full &&
              actionsOpen &&
              phoneActions.length > 0 ? (
                <ActionsSheet items={phoneActions} onClose={closeActions} />
              ) : null}
              {!bars && companion.active && bandViews.length > 0 && (
                <Companion
                  views={bandViews}
                  active={companion.active}
                  onActiveChange={companion.open}
                  /* The band is one thing, so the one way out closes what it holds:
               the conversation ends with it rather than being left running
               behind a strip nobody can see. */
                  onClose={() => {
                    companion.close()
                    assistant.close()
                  }}
                />
              )}
            </div>
            {/* Drawn by the shell rather than by a route, because the account belongs
          to the person rather than to the app they happen to be in. */}
            {accountOpen ? (
              <AccountSheet
                tab={accountTab}
                onClose={() => setAccountOpen(false)}
              />
            ) : null}
          </div>
        </TogetherContext.Provider>
      </DocBarsContext.Provider>
    </OrgScopeProvider>
  )
}
